/**
 * Finance Reports hub (FIN-13) — `/finance/reports`.
 *
 * A report switcher + a SHARED filter (month range + account selection) drive
 * nine reports. The filter lives in the URL (see `routes/finance/reports.tsx`)
 * seeded from localStorage, so it survives switching reports, the back button,
 * a refresh, and a link sent to someone else — see `report-filters.ts`.
 *
 * Every report renders SERVER analytics verbatim (design §8) — the client never
 * recomputes income/expense aggregates; it only reshapes the server response
 * into the shape its chart/table needs and serializes the DISPLAYED dataset to
 * CSV. Where a report offers more than one visualization, the alternatives
 * reshape the SAME response rather than issuing a second query.
 *
 * All chart theming flows through `components/finance/charts.tsx`; this file
 * sets no raw Recharts colors. Tokens + CSS Modules only.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import {
  useCashflow,
  useCategoryBreakdown,
  useNetWorth,
  useSavingsFlow,
  useProfessionalRecovery,
  useBalanceEvolution,
  useFinanceCategories,
  useFinanceAccounts,
  useFinanceSettings,
  categoryBreakdownQueryOptions,
  type CategoryBreakdownRowDto,
  type CategoryBreakdownDto,
  type FinanceAccountDto,
  type BalanceEvolutionWeekDto,
} from '../../api/finance';
import type { FinanceAccountType } from '@k-os/core';
import {
  formatEur,
  financeErrorCode,
  buildCategoryTree,
  ACCOUNT_TYPE_LABELS,
} from './finance-format';
import { Icon } from '@k-os/ui';
import {
  addMonths,
  monthRange,
  monthLabel,
  monthStartIso,
  monthEndIso,
  presetRange,
  toCsv,
  downloadCsv,
  type RangePreset,
} from './report-format';
import {
  REPORTS,
  accountsParam,
  defaultFilters,
  isValidRange,
  loadStoredFilters,
  persistFilters,
  type ReportFilters,
} from './report-filters';
import { Route as ReportsRoute } from '../../routes/finance/reports';
import {
  FinanceBarChart,
  FinanceLineChart,
  FinanceAreaChart,
  FinancePieChart,
  FinanceCandlestickChart,
  shortMonth,
  shortWeek,
  useChartTheme,
  type CandleDatum,
  type LineSeries,
} from '../../components/finance/charts';
import { BalanceTooltip } from '../../components/finance/BalanceTooltip';
import styles from './finance.module.css';
import rep from './Reports.module.css';

// ─── Shared filter props every report receives ────────────────────────────────

/** The shared filter, as each report consumes it. `accounts` empty = all. */
interface ReportProps {
  from: string;
  to: string;
  accounts: string[];
}

// ─── Segmented control (visualization switches, income/expense tabs) ─────────

/**
 * A small pill group for choosing between mutually exclusive views of the SAME
 * data. Used for the per-report visualization switches and the Income & expense
 * sub-tabs, so both read as "the same numbers, shown differently".
 */
function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { id: T; label: string }[];
  onChange: (id: T) => void;
  /** Optional caption rendered before the group (e.g. "View"). */
  label?: string;
}) {
  return (
    <div className={rep.segmentedWrap}>
      {label && <span className={rep.segmentedLabel}>{label}</span>}
      <div className={rep.kindToggle} role="tablist">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={value === o.id}
            className={`${rep.kindBtn} ${value === o.id ? rep.kindBtnActive : ''}`}
            onClick={() => onChange(o.id)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Shared report frame (title, CSV button, loading / empty / error slots) ───

function ReportFrame({
  title,
  description,
  onExport,
  exportDisabled,
  children,
}: {
  title: string;
  description: string;
  onExport: () => void;
  exportDisabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={rep.report}>
      <div className={rep.reportHead}>
        <div>
          <h3 className={rep.reportTitle}>{title}</h3>
          <div className={styles.muted}>{description}</div>
        </div>
        <button
          type="button"
          className="kos-btn"
          onClick={onExport}
          disabled={exportDisabled}
        >
          Export CSV
        </button>
      </div>
      {children}
    </section>
  );
}

function ChartCard({ children }: { children: React.ReactNode }) {
  return <div className={rep.chartCard}>{children}</div>;
}

// ─── Cash flow ────────────────────────────────────────────────────────────────

type CashflowViz = 'lines' | 'bars';

function CashflowReport({ from, to, accounts }: ReportProps) {
  const theme = useChartTheme();
  // Lines first: over a 12-month range two trend lines read as movement, while
  // grouped bars read as twelve separate comparisons. Bars stay available for
  // reading one month against another.
  const [viz, setViz] = useState<CashflowViz>('lines');
  const q = useCashflow(from, to, accounts);
  const months = q.data ?? [];

  const handleExport = () => {
    const rows = months.map((m) => [m.month, m.moneyIn, m.moneyOut, m.net]);
    downloadCsv(
      `cashflow_${from}_${to}.csv`,
      toCsv(['Month', 'Money in', 'Money out', 'Net'], rows),
    );
  };

  return (
    <ReportFrame
      title="Cash flow"
      description="Monthly money in vs. money out, with the net over the top."
      onExport={handleExport}
      exportDisabled={months.length === 0}
    >
      <SegmentedControl
        label="View"
        value={viz}
        onChange={setViz}
        options={[
          { id: 'lines', label: 'Trend lines' },
          { id: 'bars', label: 'Grouped bars' },
        ]}
      />
      {q.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : months.length === 0 ? (
        <div className={styles.empty}>No transactions in this range.</div>
      ) : (
        <ChartCard>
          {viz === 'lines' ? (
            <FinanceLineChart
              data={months}
              xKey="month"
              lines={[
                { key: 'moneyIn', name: 'Money in', color: theme.positive },
                { key: 'moneyOut', name: 'Money out', color: theme.negative },
                { key: 'net', name: 'Net', color: theme.net },
              ]}
              xTickFormatter={(v) => shortMonth(v)}
            />
          ) : (
            <FinanceBarChart
              data={months}
              xKey="month"
              bars={[
                { key: 'moneyIn', name: 'Money in', color: theme.positive },
                { key: 'moneyOut', name: 'Money out', color: theme.negative },
              ]}
              lines={[{ key: 'net', name: 'Net', color: theme.net }]}
              xTickFormatter={(v) => shortMonth(v)}
            />
          )}
        </ChartCard>
      )}
    </ReportFrame>
  );
}

// ─── Income & expense (hierarchical table + per-root pie) ──────────────────

interface BreakdownNode {
  row: CategoryBreakdownRowDto;
  children: BreakdownNode[];
}

function buildBreakdownTree(rows: CategoryBreakdownRowDto[]): BreakdownNode[] {
  const byId = new Map<string, BreakdownNode>();
  for (const row of rows) byId.set(row.categoryId, { row, children: [] });
  const roots: BreakdownNode[] = [];
  for (const node of byId.values()) {
    const parent = node.row.parentId ? byId.get(node.row.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sortRec = (nodes: BreakdownNode[]) => {
    nodes.sort((a, b) => b.row.total - a.row.total);
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

function BreakdownRows({
  nodes,
  expanded,
  toggle,
  depth,
}: {
  nodes: BreakdownNode[];
  expanded: Set<string>;
  toggle: (id: string) => void;
  depth: number;
}) {
  return (
    <>
      {nodes.map((node) => {
        const { row, children } = node;
        const hasChildren = children.length > 0;
        const isOpen = expanded.has(row.categoryId);
        return (
          <div key={row.categoryId}>
            <div
              className={`${rep.tableRow} ${depth === 0 ? rep.tableRowRoot : ''}`}
              style={{ paddingLeft: 12 + depth * 20 }}
            >
              <div className={rep.tableName}>
                {hasChildren ? (
                  <button
                    type="button"
                    className={rep.twisty}
                    onClick={() => toggle(row.categoryId)}
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                  >
                    {isOpen ? '▾' : '▸'}
                  </button>
                ) : (
                  <span className={rep.twistySpacer} />
                )}
                <span>{row.name}</span>
              </div>
              <span className={`${styles.num} ${rep.tableCell}`}>
                {row.total > 0 ? formatEur(row.total) : <span className={styles.muted}>—</span>}
              </span>
            </div>
            {hasChildren && isOpen && (
              <BreakdownRows
                nodes={children}
                expanded={expanded}
                toggle={toggle}
                depth={depth + 1}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

type IncomeExpenseMode = 'expense' | 'income' | 'compare';

/** Grand total for one kind = Σ root subtree totals + the uncategorized bucket.
 *  A reshape of the server rows the table already renders — NOT a recompute of
 *  the aggregate itself (design §8). */
function grandTotalOf(tree: BreakdownNode[], uncategorized: number): number {
  return tree.reduce((acc, n) => acc + n.row.total, 0) + uncategorized;
}

/** Root slices for a donut, capped at `limit` + an aggregated "Other" — the
 *  design system allows at most 5 readable slices. Uncategorized rides along as
 *  its own slice so it is never silently folded away. */
function rootSlices(
  tree: BreakdownNode[],
  uncategorized: number,
  limit: number,
): { name: string; value: number }[] {
  const roots = tree
    .filter((n) => n.row.total > 0)
    .map((n) => ({ name: n.row.name, value: n.row.total }));
  const head = roots.slice(0, limit);
  const tail = roots.slice(limit);
  const out = [...head];
  const otherTotal = tail.reduce((acc, r) => acc + r.value, 0);
  if (otherTotal > 0) out.push({ name: `Other (${tail.length})`, value: otherTotal });
  if (uncategorized > 0) out.push({ name: 'Uncategorized', value: uncategorized });
  return out;
}

/** The hierarchical table + per-root donut for ONE kind. The kind's grand total
 *  is surfaced twice — in the table header and in the donut hole — so the
 *  headline figure is readable without hovering or summing rows by eye. */
function BreakdownView({
  kind,
  tree,
  uncategorized,
  total,
}: {
  kind: 'income' | 'expense';
  tree: BreakdownNode[];
  uncategorized: number;
  total: number;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Per-root pie: total per root category (+ an Uncategorized slice).
  const pieData = useMemo(() => {
    const data = tree
      .filter((n) => n.row.total > 0)
      .map((n) => ({ name: n.row.name, value: n.row.total }));
    if (uncategorized > 0) data.push({ name: 'Uncategorized', value: uncategorized });
    return data;
  }, [tree, uncategorized]);

  const totalLabel = kind === 'income' ? 'Total income' : 'Total expense';

  return (
    <div className={rep.splitLayout}>
      <div className={`${styles.tableWrap} ${rep.breakdownTable}`}>
        <div className={rep.tableHeader}>
          <div className={rep.tableNameCol}>Category</div>
          <div className={`${styles.num} ${rep.tableCell} ${rep.tableHeaderTotal}`}>
            <span>{totalLabel}</span>
            <span className={rep.tableHeaderTotalValue}>{formatEur(total)}</span>
          </div>
        </div>
        <BreakdownRows nodes={tree} expanded={expanded} toggle={toggle} depth={0} />
        {uncategorized > 0 && (
          <div className={`${rep.tableRow} ${rep.tableRowRoot}`} style={{ paddingLeft: 12 }}>
            <div className={rep.tableName}>
              <span className={rep.twistySpacer} />
              <span className={styles.muted}>Uncategorized</span>
            </div>
            <span className={`${styles.num} ${rep.tableCell}`}>{formatEur(uncategorized)}</span>
          </div>
        )}
      </div>
      <ChartCard>
        <FinancePieChart data={pieData} centerLabel={totalLabel} centerValue={formatEur(total)} />
      </ChartCard>
    </div>
  );
}

/** Income vs. expenses for the whole selected period: did the range save or
 *  overspend, by how much, and which expense roots drove it. */
function CompareView({
  incomeTotal,
  expenseTotal,
  expenseTree,
  expenseUncategorized,
}: {
  incomeTotal: number;
  expenseTotal: number;
  expenseTree: BreakdownNode[];
  expenseUncategorized: number;
}) {
  const net = incomeTotal - expenseTotal;
  const saved = net >= 0;
  // Both bars share one scale so their lengths are directly comparable.
  const scale = Math.max(incomeTotal, expenseTotal);
  const barPct = (v: number) => (scale > 0 ? (v / scale) * 100 : 0);
  const savingsRate = incomeTotal > 0 ? (net / incomeTotal) * 100 : null;
  const spendRate = incomeTotal > 0 ? (expenseTotal / incomeTotal) * 100 : null;

  // Traffic light on share of income spent, matching the dashboard's budget bars.
  const spendTone =
    spendRate === null || spendRate <= 80
      ? rep.barGreen
      : spendRate <= 100
        ? rep.barAmber
        : rep.barRed;

  const slices = useMemo(
    () => rootSlices(expenseTree, expenseUncategorized, 5),
    [expenseTree, expenseUncategorized],
  );

  // Top expense roots by share of total expenses (+ Uncategorized if present).
  const shares = useMemo(() => {
    const roots = expenseTree
      .filter((n) => n.row.total > 0)
      .map((n) => ({ name: n.row.name, value: n.row.total }));
    if (expenseUncategorized > 0)
      roots.push({ name: 'Uncategorized', value: expenseUncategorized });
    roots.sort((a, b) => b.value - a.value);
    return roots.slice(0, 6).map((r) => ({
      ...r,
      pct: expenseTotal > 0 ? (r.value / expenseTotal) * 100 : 0,
    }));
  }, [expenseTree, expenseUncategorized, expenseTotal]);

  return (
    <>
      <div className={rep.kpiRow}>
        <div className={rep.kpi}>
          <span className={rep.kpiLabel}>Income</span>
          <span className={`${rep.kpiValue} ${rep.kpiPos}`}>{formatEur(incomeTotal)}</span>
        </div>
        <div className={rep.kpi}>
          <span className={rep.kpiLabel}>Expenses</span>
          <span className={`${rep.kpiValue} ${rep.kpiNeg}`}>{formatEur(expenseTotal)}</span>
          {spendRate !== null && (
            <span className={rep.kpiSub}>{spendRate.toFixed(1)}% of income</span>
          )}
        </div>
        <div className={rep.kpi}>
          <span className={rep.kpiLabel}>{saved ? 'Saved' : 'Overspent'}</span>
          <span className={`${rep.kpiValue} ${saved ? rep.kpiPos : rep.kpiNeg}`}>
            {formatEur(Math.abs(net))}
          </span>
          {savingsRate !== null && (
            <span className={rep.kpiSub}>
              {saved ? 'Savings rate' : 'Shortfall'} {Math.abs(savingsRate).toFixed(1)}%
            </span>
          )}
        </div>
      </div>

      <div className={rep.splitLayout}>
        <div className={rep.compareLeft}>
          <div className={rep.comparePanel}>
            <div className={rep.panelTitle}>Income vs. expenses</div>
            <div className={rep.compareBar}>
              <div className={rep.compareBarHead}>
                <span className={rep.compareBarName}>Income</span>
                <span className={`${styles.num} ${rep.compareBarFig}`}>
                  {formatEur(incomeTotal)}
                </span>
              </div>
              <div className={rep.barTrack}>
                <div
                  className={`${rep.barFill} ${rep.barGreen}`}
                  style={{ width: `${barPct(incomeTotal)}%` }}
                />
              </div>
            </div>
            <div className={rep.compareBar}>
              <div className={rep.compareBarHead}>
                <span className={rep.compareBarName}>Expenses</span>
                <span className={`${styles.num} ${rep.compareBarFig}`}>
                  {formatEur(expenseTotal)}
                </span>
              </div>
              <div className={rep.barTrack}>
                <div
                  className={`${rep.barFill} ${spendTone}`}
                  style={{ width: `${barPct(expenseTotal)}%` }}
                />
              </div>
            </div>
            <div className={`${rep.compareVerdict} ${saved ? rep.kpiPos : rep.kpiNeg}`}>
              {saved
                ? `Saved ${formatEur(net)} over the period.`
                : `Overspent by ${formatEur(-net)} over the period.`}
            </div>
          </div>

          <div className={rep.comparePanel}>
            <div className={rep.panelTitle}>Where the money went</div>
            {shares.length === 0 ? (
              <div className={styles.muted}>No expenses in this range.</div>
            ) : (
              <div className={rep.shareList}>
                {shares.map((s) => (
                  <div key={s.name} className={rep.compareBar}>
                    <div className={rep.compareBarHead}>
                      <span className={rep.compareBarName}>{s.name}</span>
                      <span className={`${styles.num} ${rep.compareBarFig}`}>
                        {formatEur(s.value)}
                        <span className={rep.sharePct}>{s.pct.toFixed(0)}%</span>
                      </span>
                    </div>
                    <div className={rep.barTrack}>
                      <div
                        className={`${rep.barFill} ${rep.barNeutral}`}
                        style={{ width: `${Math.min(s.pct, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <ChartCard>
          <FinancePieChart
            data={slices}
            centerLabel="Total expense"
            centerValue={formatEur(expenseTotal)}
          />
        </ChartCard>
      </div>
    </>
  );
}

function IncomeExpenseReport({ from, to, accounts }: ReportProps) {
  const [mode, setMode] = useState<IncomeExpenseMode>('expense');
  // Compare needs BOTH kinds; the single-kind tabs fetch only what they show.
  // The two queries share the cache with every other consumer of this endpoint,
  // so switching tabs re-reads rather than re-fetches.
  const expenseQ = useCategoryBreakdown(from, to, 'expense', {
    accountIds: accounts,
    enabled: mode === 'expense' || mode === 'compare',
  });
  const incomeQ = useCategoryBreakdown(from, to, 'income', {
    accountIds: accounts,
    enabled: mode === 'income' || mode === 'compare',
  });

  const expenseTree = useMemo(
    () => buildBreakdownTree(expenseQ.data?.categories ?? []),
    [expenseQ.data],
  );
  const incomeTree = useMemo(
    () => buildBreakdownTree(incomeQ.data?.categories ?? []),
    [incomeQ.data],
  );
  const expenseUncategorized = expenseQ.data?.uncategorizedTotal ?? 0;
  const incomeUncategorized = incomeQ.data?.uncategorizedTotal ?? 0;
  const expenseTotal = useMemo(
    () => grandTotalOf(expenseTree, expenseUncategorized),
    [expenseTree, expenseUncategorized],
  );
  const incomeTotal = useMemo(
    () => grandTotalOf(incomeTree, incomeUncategorized),
    [incomeTree, incomeUncategorized],
  );

  const isLoading =
    mode === 'compare'
      ? expenseQ.isLoading || incomeQ.isLoading
      : mode === 'expense'
        ? expenseQ.isLoading
        : incomeQ.isLoading;

  const hasData =
    mode === 'compare'
      ? incomeTotal > 0 || expenseTotal > 0
      : mode === 'expense'
        ? expenseTotal > 0
        : incomeTotal > 0;

  const handleExport = () => {
    if (mode === 'compare') {
      const share = (v: number) =>
        expenseTotal > 0 ? Number(((v / expenseTotal) * 100).toFixed(2)) : 0;
      const net = incomeTotal - expenseTotal;
      const out: Array<Array<string | number>> = [
        ['Income', incomeTotal, ''],
        ['Expenses', expenseTotal, ''],
        [net >= 0 ? 'Saved' : 'Overspent', Math.abs(net), ''],
      ];
      for (const n of expenseTree) {
        if (n.row.total > 0) out.push([`Expense: ${n.row.name}`, n.row.total, share(n.row.total)]);
      }
      if (expenseUncategorized > 0) {
        out.push(['Expense: Uncategorized', expenseUncategorized, share(expenseUncategorized)]);
      }
      downloadCsv(
        `income-vs-expenses_${from}_${to}.csv`,
        toCsv(['Item', 'Amount', 'Share of expenses %'], out),
      );
      return;
    }

    // Flatten the tree depth-first so the CSV mirrors the on-screen rollup.
    const tree = mode === 'expense' ? expenseTree : incomeTree;
    const uncategorized = mode === 'expense' ? expenseUncategorized : incomeUncategorized;
    const out: Array<Array<string | number>> = [];
    const walk = (nodes: BreakdownNode[], depth: number) => {
      for (const n of nodes) {
        out.push([`${'  '.repeat(depth)}${n.row.name}`, n.row.ownTotal, n.row.total]);
        walk(n.children, depth + 1);
      }
    };
    walk(tree, 0);
    if (uncategorized > 0) out.push(['Uncategorized', uncategorized, uncategorized]);
    out.push(['TOTAL', '', mode === 'expense' ? expenseTotal : incomeTotal]);
    downloadCsv(
      `income-expense_${mode}_${from}_${to}.csv`,
      toCsv(['Category', 'Own total', 'Subtree total'], out),
    );
  };

  const tabs: { id: IncomeExpenseMode; label: string }[] = [
    { id: 'expense', label: 'Expense' },
    { id: 'income', label: 'Income' },
    { id: 'compare', label: 'Income vs Expenses' },
  ];

  return (
    <ReportFrame
      title="Income & expense"
      description={
        mode === 'compare'
          ? 'Income against expenses for the selected period and accounts — saved or overspent, and what drove it.'
          : 'Category totals rolled up the hierarchy. Expand a root to see its children.'
      }
      onExport={handleExport}
      exportDisabled={!hasData}
    >
      <div className={rep.kindToggle}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`${rep.kindBtn} ${mode === t.id ? rep.kindBtnActive : ''}`}
            onClick={() => setMode(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : !hasData ? (
        <div className={styles.empty}>
          {mode === 'compare' ? 'No income or expenses in this range.' : `No ${mode} in this range.`}
        </div>
      ) : mode === 'compare' ? (
        <CompareView
          incomeTotal={incomeTotal}
          expenseTotal={expenseTotal}
          expenseTree={expenseTree}
          expenseUncategorized={expenseUncategorized}
        />
      ) : (
        <BreakdownView
          kind={mode}
          tree={mode === 'expense' ? expenseTree : incomeTree}
          uncategorized={mode === 'expense' ? expenseUncategorized : incomeUncategorized}
          total={mode === 'expense' ? expenseTotal : incomeTotal}
        />
      )}
    </ReportFrame>
  );
}

// ─── Category trends (hierarchical picker + monthly multi-line) ──────────────

/** Design-system ceiling for categorical series in one chart. */
const MAX_TREND_SERIES = 7;

type CategoryNode = ReturnType<typeof buildCategoryTree>[number];

/** Every id in a subtree, excluding the node itself. */
function descendantIds(node: CategoryNode): string[] {
  const out: string[] = [];
  const walk = (n: CategoryNode) => {
    for (const c of n.children) {
      out.push(c.cat.id);
      walk(c);
    }
  };
  walk(node);
  return out;
}

/**
 * What the report starts on when the user hasn't chosen yet: every level-1
 * expense root EXCEPT the professional-expenses subtree.
 *
 * The exclusion is driven by `financeSettings.professionalCategoryId` — the
 * same field the Professional recovery report keys off — rather than by
 * category name, so this stays correct in any workspace whatever its
 * categories are called. Professional spend is excluded because it is
 * reimbursable: mixed into a spending trend it reads as expenditure that was
 * never really yours.
 */
function defaultTrendSelection(
  roots: CategoryNode[],
  professionalCategoryId: string | null | undefined,
): string[] {
  return roots
    .filter((n) => n.cat.id !== professionalCategoryId)
    .slice(0, MAX_TREND_SERIES)
    .map((n) => n.cat.id);
}

/** Ids matching `query`, plus every ancestor, so matched rows keep their path. */
function searchMatches(roots: CategoryNode[], query: string): Set<string> | null {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  const keep = new Set<string>();
  const walk = (node: CategoryNode, ancestors: string[]): boolean => {
    const selfHit = node.cat.name.toLowerCase().includes(q);
    let childHit = false;
    for (const c of node.children) {
      if (walk(c, [...ancestors, node.cat.id])) childHit = true;
    }
    if (selfHit || childHit) {
      keep.add(node.cat.id);
      for (const a of ancestors) keep.add(a);
      return true;
    }
    return false;
  };
  for (const r of roots) walk(r, []);
  return keep;
}

function CategoryPickerRows({
  nodes,
  selected,
  expanded,
  matches,
  atCap,
  onToggle,
  onExpand,
}: {
  nodes: CategoryNode[];
  selected: Set<string>;
  expanded: Set<string>;
  /** null = not searching; otherwise the ids allowed to render. */
  matches: Set<string> | null;
  atCap: boolean;
  onToggle: (id: string) => void;
  onExpand: (id: string) => void;
}) {
  return (
    <>
      {nodes.map((node) => {
        const id = node.cat.id;
        if (matches && !matches.has(id)) return null;
        const hasChildren = node.children.length > 0;
        // A search forces every surviving branch open — hiding a match behind a
        // collapsed parent would make the search look broken.
        const isOpen = matches !== null || expanded.has(id);
        const isSelected = selected.has(id);
        const selectedInside = descendantIds(node).filter((d) => selected.has(d)).length;
        const disabled = !isSelected && atCap;

        return (
          <div key={id}>
            <div
              className={`${rep.pickRow} ${node.depth === 1 ? rep.pickRowRoot : ''}`}
              style={{ paddingLeft: 8 + (node.depth - 1) * 18 }}
            >
              {hasChildren ? (
                <button
                  type="button"
                  className={rep.twisty}
                  onClick={() => onExpand(id)}
                  aria-label={isOpen ? 'Collapse' : 'Expand'}
                  aria-expanded={isOpen}
                >
                  {isOpen ? '▾' : '▸'}
                </button>
              ) : (
                <span className={rep.twistySpacer} />
              )}
              <label className={`${rep.pickLabel} ${disabled ? rep.pickLabelOff : ''}`}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={disabled}
                  onChange={() => onToggle(id)}
                />
                <span className={rep.pickName}>{node.cat.name}</span>
              </label>
              {/* Only worth showing when the branch is shut — once it's open the
                  ticked boxes inside say the same thing. */}
              {!isOpen && selectedInside > 0 && (
                <span className={rep.pickBadge} title={`${selectedInside} selected inside`}>
                  {selectedInside}
                </span>
              )}
            </div>
            {hasChildren && isOpen && (
              <CategoryPickerRows
                nodes={node.children}
                selected={selected}
                expanded={expanded}
                matches={matches}
                atCap={atCap}
                onToggle={onToggle}
                onExpand={onExpand}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

function CategoryTrendsReport({
  from,
  to,
  accounts,
  selection,
  onSelectionChange,
}: ReportProps & {
  /** null = the user hasn't chosen; the default selection applies. */
  selection: string[] | null;
  onSelectionChange: (ids: string[]) => void;
}) {
  const theme = useChartTheme();
  const months = useMemo(() => monthRange(from, to), [from, to]);
  const categoriesQ = useFinanceCategories({ includeInactive: true });
  const settingsQ = useFinanceSettings();

  const roots = useMemo(
    () => buildCategoryTree(categoriesQ.data ?? [], { kind: 'expense', excludeArchived: false }),
    [categoriesQ.data],
  );

  // The default is DERIVED, not seeded into state by an effect: the screen
  // remounts this report whenever the shared filter changes, and an effect-based
  // default would either re-clobber the user's picks on every range change or
  // need a ref that the remount resets anyway.
  const effective = useMemo(
    () => selection ?? defaultTrendSelection(roots, settingsQ.data?.professionalCategoryId),
    [selection, roots, settingsQ.data],
  );
  const selectedSet = useMemo(() => new Set(effective), [effective]);
  const atCap = effective.length >= MAX_TREND_SERIES;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const matches = useMemo(() => searchMatches(roots, search), [roots, search]);

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (nodes: CategoryNode[]) => {
      for (const n of nodes) {
        map.set(n.cat.id, n.cat.name);
        walk(n.children);
      }
    };
    walk(roots);
    return map;
  }, [roots]);

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onSelectionChange(effective.filter((x) => x !== id));
    } else if (effective.length < MAX_TREND_SERIES) {
      onSelectionChange([...effective, id]);
    }
  };

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // One category-breakdown call PER month over the range, via `useQueries` so
  // the dynamic count stays hook-safe. Disabled until a category is selected so
  // we never fan out needlessly. We read the SUBTREE total per selected
  // category (matches the breakdown table's rollup semantics).
  const monthQueries = useQueries({
    queries: months.map((m) =>
      categoryBreakdownQueryOptions(m, m, 'expense', {
        enabled: effective.length > 0,
        accountIds: accounts,
      }),
    ),
  });

  const loading = monthQueries.some((q) => q.isLoading);

  // Assemble [{ month, <catId>: total, ... }] from the per-month responses.
  const updatedFingerprint = monthQueries.map((q) => q.dataUpdatedAt).join(',');
  const chartData = useMemo(() => {
    return months.map((m, i) => {
      const resp = monthQueries[i]?.data;
      const row: Record<string, string | number> = { month: m };
      for (const catId of effective) {
        const found = resp?.categories.find((c) => c.categoryId === catId);
        row[catId] = found ? found.total : 0;
      }
      return row;
    });
    // monthQueries identity changes each render; the fingerprint captures when
    // the underlying data actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, effective, updatedFingerprint]);

  const lines: LineSeries[] = effective.map((catId, i) => ({
    key: catId,
    name: nameById.get(catId) ?? catId,
    color: theme.series[i % theme.series.length] || theme.accent,
  }));

  const handleExport = () => {
    const headers = ['Month', ...effective.map((id) => nameById.get(id) ?? id)];
    const rows = chartData.map((d) => [
      String(d.month),
      ...effective.map((id) => Number(d[id] ?? 0)),
    ]);
    downloadCsv(`category-trends_${from}_${to}.csv`, toCsv(headers, rows));
  };

  return (
    <ReportFrame
      title="Category trends"
      description="Monthly spend per expense category (subtree totals). Starts on your top-level categories, minus professional spend."
      onExport={handleExport}
      exportDisabled={effective.length === 0}
    >
      <div className={rep.pickPanel}>
        <div className={rep.pickHead}>
          <div className={rep.pickSearch}>
            <Icon name="search" size={14} className={rep.pickSearchIcon} />
            <input
              type="search"
              className={styles.filterControl}
              placeholder="Find a category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Filter categories"
            />
          </div>
          <span className={styles.muted}>
            {effective.length} of {MAX_TREND_SERIES} series
          </span>
          {selection !== null && (
            <button type="button" className={rep.pickReset} onClick={() => onSelectionChange([])}>
              Clear
            </button>
          )}
        </div>
        {selection === null && !settingsQ.isLoading && !settingsQ.data?.professionalCategoryId && (
          <div className={rep.pickHint}>
            No professional-expense category is set, so the default selection can't exclude
            one.{' '}
            <Link to="/finance/settings" className={rep.pickHintLink}>
              Set it in Settings
            </Link>{' '}
            to keep reimbursable spend out of this trend (it also enables the Professional
            recovery report).
          </div>
        )}
        {roots.length === 0 ? (
          <div className={styles.muted}>No expense categories yet.</div>
        ) : (
          <div className={rep.pickTree}>
            <CategoryPickerRows
              nodes={roots}
              selected={selectedSet}
              expanded={expanded}
              matches={matches}
              atCap={atCap}
              onToggle={toggle}
              onExpand={toggleExpand}
            />
            {matches !== null && matches.size === 0 && (
              <div className={styles.muted}>No category matches “{search}”.</div>
            )}
          </div>
        )}
      </div>
      {effective.length === 0 ? (
        <div className={styles.empty}>Select one or more categories to see their trend.</div>
      ) : loading ? (
        <div className={styles.muted}>Loading…</div>
      ) : (
        <ChartCard>
          <FinanceLineChart
            data={chartData}
            xKey="month"
            lines={lines}
            xTickFormatter={(v) => shortMonth(v)}
          />
        </ChartCard>
      )}
    </ReportFrame>
  );
}

// ─── Net worth (stacked area assets vs. liabilities + net line) ────────────

function NetWorthReport({ from, to, accounts }: ReportProps) {
  const theme = useChartTheme();
  const q = useNetWorth(from, to, accounts);
  const months = useMemo(() => q.data ?? [], [q.data]);

  // Liabilities are returned negative; chart their magnitude so the stack reads
  // as "size of assets" vs "size of liabilities", with the true net as a line.
  const chartData = useMemo(
    () =>
      months.map((m) => ({
        month: m.month,
        assets: m.assets,
        liabilities: Math.abs(m.liabilities),
        net: m.net,
      })),
    [months],
  );

  const handleExport = () => {
    const rows = months.map((m) => [m.month, m.assets, m.liabilities, m.net]);
    downloadCsv(
      `net-worth_${from}_${to}.csv`,
      toCsv(['Month', 'Assets', 'Liabilities', 'Net'], rows),
    );
  };

  return (
    <ReportFrame
      title="Net worth"
      description="Assets vs. liabilities per month-end, with the net worth line."
      onExport={handleExport}
      exportDisabled={months.length === 0}
    >
      {q.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : months.length === 0 ? (
        <div className={styles.empty}>No accounts to report on yet.</div>
      ) : (
        <ChartCard>
          <FinanceAreaChart
            data={chartData}
            xKey="month"
            areas={[
              { key: 'assets', name: 'Assets', color: theme.positive, stackId: 'nw' },
              { key: 'liabilities', name: 'Liabilities', color: theme.negative, stackId: 'nw' },
            ]}
            lines={[{ key: 'net', name: 'Net worth', color: theme.net }]}
            xTickFormatter={(v) => shortMonth(v)}
          />
        </ChartCard>
      )}
    </ReportFrame>
  );
}

// ─── Professional recovery (spent vs recovered bars + outstanding line) ────

function ProfessionalRecoveryReport({ from, to, accounts }: ReportProps) {
  const theme = useChartTheme();
  const q = useProfessionalRecovery(from, to, { accountIds: accounts });
  const settingsRequired = q.isError && financeErrorCode(q.error) === 'settings_required';
  const data = q.data;
  const months = useMemo(() => data?.months ?? [], [data]);

  // Cumulative outstanding line per month (running spent − recovered), so the
  // chart shows the balance trending alongside the monthly bars.
  const chartData = useMemo(() => {
    let running = 0;
    return months.map((m) => {
      running += m.spent - m.recovered;
      return { month: m.month, spent: m.spent, recovered: m.recovered, outstanding: running };
    });
  }, [months]);

  const handleExport = () => {
    const rows = chartData.map((m) => [m.month, m.spent, m.recovered, m.outstanding]);
    downloadCsv(
      `professional-recovery_${from}_${to}.csv`,
      toCsv(['Month', 'Spent', 'Recovered', 'Outstanding (cumulative)'], rows),
    );
  };

  return (
    <ReportFrame
      title="Professional recovery"
      description="Professional spend vs. reimbursement income, with the outstanding balance."
      onExport={handleExport}
      exportDisabled={months.length === 0}
    >
      {settingsRequired ? (
        <div className={styles.empty}>
          Set a professional-expense category and a reimbursement category in{' '}
          <strong>Settings</strong> to enable this report.
        </div>
      ) : q.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : months.length === 0 ? (
        <div className={styles.empty}>No professional activity in this range.</div>
      ) : (
        <>
          <div className={rep.outstandingRow}>
            <span className={styles.muted}>Outstanding (lifetime):</span>
            <span className={`${styles.num} ${rep.outstandingFig}`}>
              {formatEur(data?.outstanding ?? 0)}
            </span>
          </div>
          <ChartCard>
            <FinanceBarChart
              data={chartData}
              xKey="month"
              bars={[
                { key: 'spent', name: 'Spent', color: theme.negative },
                { key: 'recovered', name: 'Recovered', color: theme.positive },
              ]}
              lines={[{ key: 'outstanding', name: 'Outstanding', color: theme.net }]}
              xTickFormatter={(v) => shortMonth(v)}
            />
          </ChartCard>
        </>
      )}
    </ReportFrame>
  );
}

// ─── Savings flow (monthly net into savings, bars or cumulative line) ─────────

type SavingsViz = 'bars' | 'cumulative';

function SavingsFlowReport({ from, to, accounts }: ReportProps) {
  const theme = useChartTheme();
  const [viz, setViz] = useState<SavingsViz>('bars');
  const q = useSavingsFlow(from, to, accounts);
  const months = useMemo(() => q.data ?? [], [q.data]);
  const hasData = months.some((m) => m.inflow !== 0 || m.outflow !== 0);

  // Running total of the server's monthly net. A reshape of the response, not a
  // second aggregate — the same numbers, read as "how much has accumulated".
  const cumulative = useMemo(() => {
    let running = 0;
    return months.map((m) => {
      running += m.net;
      return { month: m.month, net: m.net, cumulative: running };
    });
  }, [months]);

  const handleExport = () => {
    const rows = cumulative.map((m, i) => [
      m.month,
      months[i]?.inflow ?? 0,
      months[i]?.outflow ?? 0,
      m.net,
      m.cumulative,
    ]);
    downloadCsv(
      `savings-flow_${from}_${to}.csv`,
      toCsv(['Month', 'Inflow', 'Outflow', 'Net', 'Cumulative net'], rows),
    );
  };

  return (
    <ReportFrame
      title="Savings flow"
      description="Net money moved into savings/investment accounts each month."
      onExport={handleExport}
      exportDisabled={!hasData}
    >
      <SegmentedControl
        label="View"
        value={viz}
        onChange={setViz}
        options={[
          { id: 'bars', label: 'Monthly bars' },
          { id: 'cumulative', label: 'Cumulative line' },
        ]}
      />
      {q.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : !hasData ? (
        <div className={styles.empty}>No savings transfers in this range.</div>
      ) : (
        <ChartCard>
          {viz === 'bars' ? (
            <FinanceBarChart
              data={months}
              xKey="month"
              bars={[{ key: 'net', name: 'Net into savings', color: theme.net }]}
              xTickFormatter={(v) => shortMonth(v)}
            />
          ) : (
            <FinanceLineChart
              data={cumulative}
              xKey="month"
              lines={[
                { key: 'cumulative', name: 'Cumulative into savings', color: theme.net },
                { key: 'net', name: 'Monthly net', color: theme.statusSomeday },
              ]}
              xTickFormatter={(v) => shortMonth(v)}
            />
          )}
        </ChartCard>
      )}
    </ReportFrame>
  );
}

// ─── Account balance evolution (weekly OHLC candles, shared account filter) ───

function BalanceEvolutionReport({ from, to, accounts }: ReportProps) {
  // The endpoint takes ISO dates, not month keys — widen the shared month range
  // to cover the whole first and last month.
  const fromIso = monthStartIso(from);
  const toIso = monthEndIso(to);
  // Empty selection means "all accounts" everywhere else in the hub; this
  // endpoint spells that as an omitted param.
  const q = useBalanceEvolution(fromIso, toIso, accounts.length > 0 ? accounts : undefined);
  const weeks = useMemo(() => q.data ?? [], [q.data]);

  // Index weeks by their x key so the tooltip can recover the full week object
  // (with the per-account breakdown) from the candle datum.
  const weekByKey = useMemo(() => {
    const m = new Map<string, BalanceEvolutionWeekDto>();
    for (const w of weeks) m.set(w.weekStart, w);
    return m;
  }, [weeks]);

  const candles: CandleDatum[] = useMemo(
    () =>
      weeks.map((w) => ({
        x: w.weekStart,
        open: w.open,
        close: w.close,
        high: w.high,
        low: w.low,
      })),
    [weeks],
  );

  const handleExport = () => {
    const rows = weeks.map((w) => [w.weekStart, w.open, w.close, w.high, w.low]);
    downloadCsv(
      `balance-evolution_${from}_${to}.csv`,
      toCsv(['Week starting', 'Open', 'Close', 'High', 'Low'], rows),
    );
  };

  return (
    <ReportFrame
      title="Account balance evolution"
      description="Weekly open / close / high / low of the selected accounts' combined balance. Includes transfer legs — these are actual balances."
      onExport={handleExport}
      exportDisabled={weeks.length === 0}
    >
      {q.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : weeks.length === 0 ? (
        <div className={styles.empty}>No balance history in this range.</div>
      ) : (
        <ChartCard>
          <FinanceCandlestickChart
            data={candles}
            xTickFormatter={(v) => shortWeek(v)}
            tooltip={(datum) => (
              <BalanceTooltip week={datum ? (weekByKey.get(datum.x) ?? null) : null} />
            )}
          />
        </ChartCard>
      )}
    </ReportFrame>
  );
}

// ─── Period comparison (this range vs the same span immediately before) ───────

/** The equally-long month range ending immediately before `from`. */
function previousSpan(from: string, to: string): { from: string; to: string } {
  const span = monthRange(from, to).length || 1;
  return { from: addMonths(from, -span), to: addMonths(to, -span) };
}

/** Axis labels only — the table above carries the full category name. */
function truncateLabel(value: string): string {
  return value.length > 14 ? `${value.slice(0, 13)}…` : value;
}

interface ComparisonRow {
  name: string;
  current: number;
  previous: number;
  delta: number;
  /** null when the previous period was zero — a % change would be undefined. */
  pct: number | null;
}

function PeriodComparisonReport({ from, to, accounts }: ReportProps) {
  const [kind, setKind] = useState<'expense' | 'income'>('expense');
  const prev = useMemo(() => previousSpan(from, to), [from, to]);

  const currentQ = useCategoryBreakdown(from, to, kind, { accountIds: accounts });
  const previousQ = useCategoryBreakdown(prev.from, prev.to, kind, { accountIds: accounts });

  // Compare ROOT categories only: a level-3 leaf moving €40 is noise next to a
  // root moving €400, and the roots already roll their subtrees up.
  const rows = useMemo<ComparisonRow[]>(() => {
    const roll = (data: CategoryBreakdownDto | undefined) => {
      const map = new Map<string, number>();
      for (const c of data?.categories ?? []) {
        if (c.parentId === null) map.set(c.name, c.total);
      }
      if (data && data.uncategorizedTotal > 0) {
        map.set('Uncategorized', data.uncategorizedTotal);
      }
      return map;
    };
    const cur = roll(currentQ.data);
    const old = roll(previousQ.data);
    const names = new Set([...cur.keys(), ...old.keys()]);
    const out: ComparisonRow[] = [];
    for (const name of names) {
      const current = cur.get(name) ?? 0;
      const previous = old.get(name) ?? 0;
      if (current === 0 && previous === 0) continue;
      out.push({
        name,
        current,
        previous,
        delta: current - previous,
        pct: previous > 0 ? ((current - previous) / previous) * 100 : null,
      });
    }
    // Biggest absolute movement first — that is what the report is for.
    out.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    return out;
  }, [currentQ.data, previousQ.data]);

  const totals = useMemo(() => {
    const current = rows.reduce((a, r) => a + r.current, 0);
    const previous = rows.reduce((a, r) => a + r.previous, 0);
    return { current, previous, delta: current - previous };
  }, [rows]);

  const isLoading = currentQ.isLoading || previousQ.isLoading;
  const theme = useChartTheme();

  const chartData = useMemo(
    () => rows.slice(0, 8).map((r) => ({ name: r.name, current: r.current, previous: r.previous })),
    [rows],
  );

  const handleExport = () => {
    const out = rows.map((r) => [
      r.name,
      r.previous,
      r.current,
      r.delta,
      r.pct === null ? '' : Number(r.pct.toFixed(2)),
    ]);
    downloadCsv(
      `period-comparison_${kind}_${from}_${to}.csv`,
      toCsv(
        [
          'Category',
          `Previous (${prev.from} – ${prev.to})`,
          `Current (${from} – ${to})`,
          'Change',
          'Change %',
        ],
        out,
      ),
    );
  };

  // "Up" is bad for expenses and good for income — colour by meaning, not sign.
  const toneOf = (delta: number) => {
    if (delta === 0) return rep.deltaFlat;
    const good = kind === 'income' ? delta > 0 : delta < 0;
    return good ? rep.deltaGood : rep.deltaBad;
  };

  return (
    <ReportFrame
      title="Period comparison"
      description={`${monthLabel(from)} – ${monthLabel(to)} against the same span immediately before (${monthLabel(prev.from)} – ${monthLabel(prev.to)}).`}
      onExport={handleExport}
      exportDisabled={rows.length === 0}
    >
      <SegmentedControl
        value={kind}
        onChange={setKind}
        options={[
          { id: 'expense', label: 'Expense' },
          { id: 'income', label: 'Income' },
        ]}
      />
      {isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>No {kind} in either period.</div>
      ) : (
        <>
          <div className={rep.kpiRow}>
            <div className={rep.kpi}>
              <span className={rep.kpiLabel}>Previous</span>
              <span className={rep.kpiValue}>{formatEur(totals.previous)}</span>
            </div>
            <div className={rep.kpi}>
              <span className={rep.kpiLabel}>Current</span>
              <span className={rep.kpiValue}>{formatEur(totals.current)}</span>
            </div>
            <div className={rep.kpi}>
              <span className={rep.kpiLabel}>Change</span>
              <span className={`${rep.kpiValue} ${toneOf(totals.delta)}`}>
                {totals.delta >= 0 ? '+' : '−'}
                {formatEur(Math.abs(totals.delta))}
              </span>
              {totals.previous > 0 && (
                <span className={rep.kpiSub}>
                  {((totals.delta / totals.previous) * 100).toFixed(1)}% vs. previous
                </span>
              )}
            </div>
          </div>

          <div className={`${styles.tableWrap} ${rep.breakdownTable} ${rep.compareTable}`}>
            <div className={`${rep.tableHeader} ${rep.compareGrid}`}>
              <div>Category</div>
              <div className={styles.num}>Previous</div>
              <div className={styles.num}>Current</div>
              <div className={styles.num}>Change</div>
            </div>
            {rows.map((r) => (
              <div key={r.name} className={`${rep.tableRow} ${rep.compareGrid}`}>
                <span className={rep.compareName}>{r.name}</span>
                <span className={`${styles.num} ${styles.muted}`}>{formatEur(r.previous)}</span>
                <span className={styles.num}>{formatEur(r.current)}</span>
                <span className={`${styles.num} ${toneOf(r.delta)}`}>
                  {r.delta >= 0 ? '+' : '−'}
                  {formatEur(Math.abs(r.delta))}
                  {r.pct !== null && (
                    <span className={rep.comparePct}>
                      {r.pct >= 0 ? '+' : '−'}
                      {Math.abs(r.pct).toFixed(0)}%
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
          <ChartCard>
            <FinanceBarChart
              data={chartData}
              xKey="name"
              bars={[
                { key: 'previous', name: 'Previous', color: theme.statusSomeday },
                { key: 'current', name: 'Current', color: theme.accent },
              ]}
              xTickFormatter={truncateLabel}
            />
          </ChartCard>
        </>
      )}
    </ReportFrame>
  );
}

// ─── Savings rate (monthly net ÷ income, with the period average) ─────────────

function SavingsRateReport({ from, to, accounts }: ReportProps) {
  const theme = useChartTheme();
  const q = useCashflow(from, to, accounts);
  const months = useMemo(() => q.data ?? [], [q.data]);

  // Percentage per month, plus a flat period-average reference line. Months with
  // no income are left null so Recharts breaks the line instead of plotting a
  // meaningless 0% — a month with no income has no savings RATE.
  const { chartData, average, best, worst } = useMemo(() => {
    const withIncome = months.filter((m) => m.moneyIn > 0);
    const totalIn = months.reduce((a, m) => a + m.moneyIn, 0);
    const totalNet = months.reduce((a, m) => a + m.net, 0);
    const avg = totalIn > 0 ? (totalNet / totalIn) * 100 : 0;
    const rates = withIncome.map((m) => ({ month: m.month, rate: (m.net / m.moneyIn) * 100 }));
    return {
      chartData: months.map((m) => ({
        month: m.month,
        rate: m.moneyIn > 0 ? (m.net / m.moneyIn) * 100 : null,
        average: avg,
      })),
      average: avg,
      best: rates.length ? rates.reduce((a, b) => (b.rate > a.rate ? b : a)) : null,
      worst: rates.length ? rates.reduce((a, b) => (b.rate < a.rate ? b : a)) : null,
    };
  }, [months]);

  const hasData = months.some((m) => m.moneyIn > 0);

  const handleExport = () => {
    const rows = months.map((m) => [
      m.month,
      m.moneyIn,
      m.net,
      m.moneyIn > 0 ? Number(((m.net / m.moneyIn) * 100).toFixed(2)) : '',
    ]);
    downloadCsv(
      `savings-rate_${from}_${to}.csv`,
      toCsv(['Month', 'Income', 'Net', 'Savings rate %'], rows),
    );
  };

  return (
    <ReportFrame
      title="Savings rate"
      description="Net ÷ income per month, as a percentage. Months without income are left blank rather than plotted as zero."
      onExport={handleExport}
      exportDisabled={!hasData}
    >
      {q.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : !hasData ? (
        <div className={styles.empty}>No income in this range.</div>
      ) : (
        <>
          <div className={rep.kpiRow}>
            <div className={rep.kpi}>
              <span className={rep.kpiLabel}>Period average</span>
              <span className={`${rep.kpiValue} ${average >= 0 ? rep.kpiPos : rep.kpiNeg}`}>
                {average.toFixed(1)}%
              </span>
              <span className={rep.kpiSub}>Total net ÷ total income</span>
            </div>
            {best && (
              <div className={rep.kpi}>
                <span className={rep.kpiLabel}>Best month</span>
                <span className={`${rep.kpiValue} ${rep.kpiPos}`}>{best.rate.toFixed(1)}%</span>
                <span className={rep.kpiSub}>{monthLabel(best.month)}</span>
              </div>
            )}
            {worst && (
              <div className={rep.kpi}>
                <span className={rep.kpiLabel}>Worst month</span>
                <span className={`${rep.kpiValue} ${worst.rate >= 0 ? rep.kpiPos : rep.kpiNeg}`}>
                  {worst.rate.toFixed(1)}%
                </span>
                <span className={rep.kpiSub}>{monthLabel(worst.month)}</span>
              </div>
            )}
          </div>
          <ChartCard>
            <FinanceLineChart
              data={chartData}
              xKey="month"
              lines={[
                { key: 'rate', name: 'Savings rate', color: theme.net },
                { key: 'average', name: 'Period average', color: theme.statusSomeday },
              ]}
              xTickFormatter={(v) => shortMonth(v)}
              yTickFormatter={(v) => `${Math.round(v)}%`}
              valueFormatter={(v) => `${v.toFixed(1)}%`}
            />
          </ChartCard>
        </>
      )}
    </ReportFrame>
  );
}

// ─── Account filter (quick-select groups + parent → children checkboxes) ─────

interface AccountTreeNode {
  account: FinanceAccountDto;
  children: FinanceAccountDto[];
}

/** Active (non-archived) accounts grouped parent → children, one level only,
 *  matching `financeAccounts.parentAccountId`. Root order preserved from the
 *  API; children sorted by name under their parent. */
function buildAccountGroups(accounts: FinanceAccountDto[]): AccountTreeNode[] {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const childrenByParent = new Map<string, FinanceAccountDto[]>();
  for (const a of accounts) {
    if (a.parentAccountId && byId.has(a.parentAccountId)) {
      const list = childrenByParent.get(a.parentAccountId) ?? [];
      list.push(a);
      childrenByParent.set(a.parentAccountId, list);
    }
  }
  for (const list of childrenByParent.values()) list.sort((a, b) => a.name.localeCompare(b.name));

  const roots = accounts.filter((a) => !a.parentAccountId || !byId.has(a.parentAccountId));
  return roots.map((account) => ({
    account,
    children: childrenByParent.get(account.id) ?? [],
  }));
}

/** One quick-select button: a named set of account ids to jump straight to. */
interface AccountQuickGroup {
  key: string;
  label: string;
  ids: string[];
}

/**
 * Quick-select groups derived from the account list itself.
 *
 * Owner groups come from `ownerLabel`, which is free text ("JC", "TC",
 * "joint"…) — so they are discovered from the data and de-duplicated
 * case-insensitively rather than enumerated from a fixed list. Type groups come
 * from the structured `type` enum. Both are omitted when they wouldn't
 * partition anything (a single group covering every account is just "All").
 */
function accountQuickGroups(accounts: FinanceAccountDto[]): {
  owners: AccountQuickGroup[];
  types: AccountQuickGroup[];
} {
  const byOwner = new Map<string, { label: string; ids: string[] }>();
  for (const a of accounts) {
    const raw = a.ownerLabel.trim();
    if (!raw) continue;
    const key = raw.toLowerCase();
    const entry = byOwner.get(key);
    // Keep the first spelling seen as the display label.
    if (entry) entry.ids.push(a.id);
    else byOwner.set(key, { label: raw, ids: [a.id] });
  }

  const byType = new Map<FinanceAccountType, string[]>();
  for (const a of accounts) {
    const ids = byType.get(a.type) ?? [];
    ids.push(a.id);
    byType.set(a.type, ids);
  }

  const owners =
    byOwner.size > 1
      ? [...byOwner.entries()].map(([key, v]) => ({ key: `owner:${key}`, label: v.label, ids: v.ids }))
      : [];
  const types =
    byType.size > 1
      ? [...byType.entries()].map(([type, ids]) => ({
          key: `type:${type}`,
          label: ACCOUNT_TYPE_LABELS[type],
          ids,
        }))
      : [];

  owners.sort((a, b) => a.label.localeCompare(b.label));
  types.sort((a, b) => a.label.localeCompare(b.label));
  return { owners, types };
}

function AccountPicker({
  accounts,
  selected,
  onChange,
}: {
  accounts: FinanceAccountDto[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const groups = useMemo(() => buildAccountGroups(accounts), [accounts]);
  const quick = useMemo(() => accountQuickGroups(accounts), [accounts]);

  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggle = (id: string) => {
    if (selectedSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  // A group button REPLACES the selection — "show me the joint accounts" is a
  // jump, not an accumulation. Individual checkboxes stay available for
  // adjusting afterwards, and a group already showing exactly is a no-op.
  const isGroupActive = (ids: string[]) =>
    selected.length === ids.length && ids.every((id) => selectedSet.has(id));

  return (
    <div className={rep.accountPicker} ref={wrapRef}>
      <span className={rep.rangeLabel}>Accounts</span>
      <button
        type="button"
        className={`${styles.filterControl} ${rep.accountPickerBtn}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{accountsLabel(accounts, selected)}</span>
        <Icon
          name="chevronDown"
          size={14}
          className={`${rep.accountPickerChevron} ${isOpen ? rep.accountPickerChevronOpen : ''}`}
        />
      </button>
      {isOpen && (
        <div className={rep.accountPickerPanel} role="listbox" aria-multiselectable="true">
          <div className={rep.quickRow}>
            <button
              type="button"
              className={`${rep.quickChip} ${selected.length === 0 ? rep.quickChipActive : ''}`}
              onClick={() => onChange([])}
            >
              All
            </button>
            <button
              type="button"
              className={rep.quickChip}
              onClick={() => onChange(accounts.map((a) => a.id))}
              disabled={accounts.length === 0}
            >
              None
            </button>
          </div>
          {quick.owners.length > 0 && (
            <>
              <div className={rep.quickHead}>Owner</div>
              <div className={rep.quickRow}>
                {quick.owners.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    className={`${rep.quickChip} ${isGroupActive(g.ids) ? rep.quickChipActive : ''}`}
                    onClick={() => onChange(g.ids)}
                  >
                    {g.label}
                    <span className={rep.quickCount}>{g.ids.length}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {quick.types.length > 0 && (
            <>
              <div className={rep.quickHead}>Type</div>
              <div className={rep.quickRow}>
                {quick.types.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    className={`${rep.quickChip} ${isGroupActive(g.ids) ? rep.quickChipActive : ''}`}
                    onClick={() => onChange(g.ids)}
                  >
                    {g.label}
                    <span className={rep.quickCount}>{g.ids.length}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          <div className={rep.quickDivider} />
          {groups.length === 0 ? (
            <div className={styles.muted}>No accounts yet.</div>
          ) : (
            groups.map(({ account, children }) => (
              <div key={account.id}>
                <label className={rep.accountPickerRow}>
                  <input
                    type="checkbox"
                    checked={selectedSet.has(account.id)}
                    onChange={() => toggle(account.id)}
                  />
                  <span>{account.name}</span>
                  {children.length > 0 && (
                    <span className={rep.accountPickerHint}>includes sub-accounts</span>
                  )}
                </label>
                {children.map((child) => (
                  <label
                    key={child.id}
                    className={`${rep.accountPickerRow} ${rep.accountPickerRowChild}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSet.has(child.id)}
                      onChange={() => toggle(child.id)}
                    />
                    <span>{child.name}</span>
                  </label>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** "All accounts" / "Joint (3)" / "4 of 11 accounts" for the picker button and
 *  the filter summary — naming the matching group when the selection is exactly
 *  one, since that is how the user picked it. */
function accountsLabel(accounts: FinanceAccountDto[], selected: string[]): string {
  if (selected.length === 0) return 'All accounts';
  const { owners, types } = accountQuickGroups(accounts);
  const selectedSet = new Set(selected);
  for (const g of [...owners, ...types]) {
    if (g.ids.length === selected.length && g.ids.every((id) => selectedSet.has(id))) {
      return `${g.label} (${g.ids.length})`;
    }
  }
  return `${selected.length} of ${accounts.length} accounts`;
}

// ─── Shared filter bar (sticky — the same filter drives every report) ─────────

function FilterBar({
  filters,
  accounts,
  onChange,
  onReset,
}: {
  filters: ReportFilters;
  accounts: FinanceAccountDto[];
  onChange: (patch: Partial<ReportFilters>) => void;
  onReset: () => void;
}) {
  const presets: { id: RangePreset; label: string }[] = [
    { id: 'this-year', label: 'This year' },
    { id: 'last-12', label: 'Last 12 months' },
    { id: 'last-year', label: 'Last year' },
  ];

  const { from, to } = filters;
  const monthCount = monthRange(from, to).length;
  const defaults = defaultFilters();
  const isDefault =
    from === defaults.from && to === defaults.to && filters.accounts.length === 0;

  return (
    <div className={rep.filterBar}>
      <div className={rep.filterBarHead}>
        <span className={rep.filterScope}>
          <Icon name="filter" size={13} />
          Filters — applied to every report
        </span>
        {!isDefault && (
          <button type="button" className={rep.filterReset} onClick={onReset}>
            Reset
          </button>
        )}
      </div>

      <div className={rep.filterRow}>
        <label className={rep.rangeField}>
          <span className={rep.rangeLabel}>From</span>
          <input
            type="month"
            className={styles.filterControl}
            value={from}
            max={to}
            onChange={(e) => onChange({ from: e.target.value })}
          />
        </label>
        <label className={rep.rangeField}>
          <span className={rep.rangeLabel}>To</span>
          <input
            type="month"
            className={styles.filterControl}
            value={to}
            min={from}
            onChange={(e) => onChange({ to: e.target.value })}
          />
        </label>
        <div className={rep.presetRow}>
          {presets.map((p) => {
            const r = presetRange(p.id);
            const active = r.from === from && r.to === to;
            return (
              <button
                key={p.id}
                type="button"
                className={`${styles.iconBtn} ${active ? rep.presetActive : ''}`}
                onClick={() => onChange({ from: r.from, to: r.to })}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <AccountPicker
          accounts={accounts}
          selected={filters.accounts}
          onChange={(ids) => onChange({ accounts: ids })}
        />
      </div>

      <div className={rep.filterSummary}>
        <span className={rep.filterChip}>
          {monthLabel(from)} – {monthLabel(to)}
          <span className={rep.filterChipSub}>
            {monthCount} month{monthCount === 1 ? '' : 's'}
          </span>
        </span>
        <span className={rep.filterChip}>{accountsLabel(accounts, filters.accounts)}</span>
      </div>
    </div>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export function FinanceReportsScreen() {
  const search = ReportsRoute.useSearch();
  const navigate = useNavigate({ from: '/finance/reports' });
  const accountsQ = useFinanceAccounts();
  const accounts = useMemo(() => accountsQ.data ?? [], [accountsQ.data]);

  // Seed once from localStorage, so arriving at a bare /finance/reports restores
  // the filter you last worked with. Captured on first render (not read on every
  // render) so a later write can't retroactively change what this visit resolved.
  const storedRef = useRef<Partial<ReportFilters> | null>(null);
  if (storedRef.current === null) storedRef.current = loadStoredFilters();
  const stored = storedRef.current;
  const defaults = defaultFilters();

  // URL → localStorage → defaults. Rendering from `search` (rather than mirroring
  // it into state) is what makes the back button step through filter changes.
  const filters: ReportFilters = useMemo(() => {
    const accountsFromUrl =
      search.accounts !== undefined ? search.accounts.split(',').filter(Boolean) : undefined;
    return {
      report: search.report ?? stored.report ?? defaults.report,
      from: search.from ?? stored.from ?? defaults.from,
      to: search.to ?? stored.to ?? defaults.to,
      accounts: accountsFromUrl ?? stored.accounts ?? defaults.accounts,
    };
    // `defaults` is recreated per render but its values are stable within a
    // calendar month, and `stored` is frozen in the ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.report, search.from, search.to, search.accounts]);

  // Mirror the resolved filter back so the next visit starts where this one
  // left off, whether it was resolved from the URL or seeded from storage.
  useEffect(() => {
    persistFilters(filters);
  }, [filters]);

  // Prune account ids that no longer exist (archived elsewhere, or a stale
  // stored/pasted id). Left in place they'd silently filter every query on an
  // id the user can't see or clear.
  useEffect(() => {
    if (!accountsQ.data || filters.accounts.length === 0) return;
    const valid = new Set(accountsQ.data.map((a) => a.id));
    const pruned = filters.accounts.filter((id) => valid.has(id));
    if (pruned.length !== filters.accounts.length) {
      const param = accountsParam(pruned);
      void navigate({
        // `exactOptionalPropertyTypes` forbids an explicit `accounts: undefined`,
        // so drop the key entirely when the pruned selection is empty (= all).
        search: (prev) => {
          const { accounts: _drop, ...rest } = prev;
          return param === undefined ? rest : { ...rest, accounts: param };
        },
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsQ.data, filters.accounts]);

  /** Filter edits replace the history entry; switching report pushes one, so
   *  Back returns to the report you came from rather than replaying keystrokes. */
  const applyFilters = (patch: Partial<ReportFilters>) => {
    const next = { ...filters, ...patch };
    const param = accountsParam(next.accounts);
    void navigate({
      search: {
        report: next.report,
        from: next.from,
        to: next.to,
        // Omitted rather than set to undefined: an all-accounts selection has no
        // param, and `exactOptionalPropertyTypes` rejects the explicit form.
        ...(param === undefined ? {} : { accounts: param }),
      },
      replace: patch.report === undefined,
    });
  };

  const resetFilters = () => {
    const d = defaultFilters();
    applyFilters({ from: d.from, to: d.to, accounts: [] });
  };

  // Category-trends selection lives HERE, not in the report: the report is
  // remounted whenever the shared filter changes (see `reportKey`), and a
  // selection kept inside it would be wiped every time the range moved.
  // `null` means "user hasn't chosen" — the report derives its own default.
  const [trendSelection, setTrendSelection] = useState<string[] | null>(null);

  const { report, from, to } = filters;
  const validRange = isValidRange(from, to);

  // Remount the active report when the range or account filter changes so
  // per-month query fans (category trends) re-evaluate cleanly.
  const reportKey = `${report}:${from}:${to}:${filters.accounts.join(',')}`;
  const reportProps: ReportProps = { from, to, accounts: filters.accounts };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Reports</h2>
          <div className={styles.subtitle}>
            {monthLabel(from)} – {monthLabel(to)}
          </div>
        </div>
      </header>

      <FilterBar
        filters={filters}
        accounts={accounts}
        onChange={applyFilters}
        onReset={resetFilters}
      />

      <div className={rep.switcher}>
        {REPORTS.map((r) => (
          <button
            key={r.id}
            type="button"
            className={`${rep.switchBtn} ${report === r.id ? rep.switchBtnActive : ''}`}
            onClick={() => applyFilters({ report: r.id })}
          >
            {r.label}
          </button>
        ))}
      </div>

      {!validRange ? (
        <div className={styles.empty}>
          Pick a valid month range (From must be on or before To).
        </div>
      ) : (
        <div key={reportKey}>
          {report === 'cashflow' && <CashflowReport {...reportProps} />}
          {report === 'income-expense' && <IncomeExpenseReport {...reportProps} />}
          {report === 'period-comparison' && <PeriodComparisonReport {...reportProps} />}
          {report === 'category-trends' && (
            <CategoryTrendsReport
              {...reportProps}
              selection={trendSelection}
              onSelectionChange={setTrendSelection}
            />
          )}
          {report === 'balance-evolution' && <BalanceEvolutionReport {...reportProps} />}
          {report === 'net-worth' && <NetWorthReport {...reportProps} />}
          {report === 'savings-flow' && <SavingsFlowReport {...reportProps} />}
          {report === 'savings-rate' && <SavingsRateReport {...reportProps} />}
          {report === 'professional-recovery' && <ProfessionalRecoveryReport {...reportProps} />}
        </div>
      )}
    </div>
  );
}
